import { captureException } from '@sentry/browser';
import { useTolgee } from '@tolgee/react';
import { useState, useEffect } from 'react';
import { ChevronDownIcon, ChevronUpIcon, LanguageIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';

interface Language {
  id: string;
  name: string;
  languageCode: string;
  country: string;
  flag: string;
}

const languages: Language[] = [
  { id: 'en', name: 'English', languageCode: 'en', country: 'United States', flag: 'us' },
  { id: 'de', name: 'Deutsch', languageCode: 'de', country: 'Deutschland', flag: 'de' },
  { id: 'vi', name: 'Tiếng Việt', languageCode: 'vi', country: 'Việt Nam', flag: 'vn' },
  { id: 'pl', name: 'Polski', languageCode: 'pl', country: 'Polska', flag: 'pl' },
];

export default function SidebarLanguageSelector() {
  const [isOpen, setIsOpen] = useState(false);
  const { changeLanguage } = useTolgee(['language']);

  const currentLanguage = (() => {
    const savedLanguage = localStorage.getItem('language') || 'de';
    return languages.find((lang) => lang.languageCode === savedLanguage) || languages[1];
  })();

  useEffect(() => {
    // Initialize language on mount
    if (currentLanguage) {
      changeLanguage(currentLanguage.languageCode).catch(captureException);
    }
  }, []);

  const handleLanguageChange = (language: Language) => {
    changeLanguage(language.languageCode).catch(captureException);
    localStorage.setItem('language', language.languageCode);
    setIsOpen(false);
  };

  return (
    <li className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={classNames(
          'group -mx-2 flex w-full items-center gap-x-3 rounded-md p-3 text-sm font-semibold leading-6',
          'text-white hover:bg-gray-500 hover:text-white transition-colors duration-150',
          'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white',
          // Larger touch target for mobile
          'min-h-[48px]',
        )}
        type="button"
      >
        <LanguageIcon className="h-6 w-6 shrink-0 text-white" aria-hidden="true" />
        <div className="flex flex-1 items-center justify-between">
          <div className="flex items-center gap-2">
            <img
              src={`https://flagcdn.com/24x18/${currentLanguage.flag}.png`}
              srcSet={`https://flagcdn.com/48x36/${currentLanguage.flag}.png 2x, https://flagcdn.com/72x54/${currentLanguage.flag}.png 3x`}
              width="24"
              height="18"
              alt={currentLanguage.country}
              className="rounded shadow-sm"
            />
            <span className="text-white">{currentLanguage.name}</span>
          </div>
          {isOpen ? (
            <ChevronUpIcon className="h-5 w-5 text-white" aria-hidden="true" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-white" aria-hidden="true" />
          )}
        </div>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute left-0 right-0 bottom-full z-50 mb-1 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
          <ul className="py-1">
            {languages.map((language) => (
              <li key={language.id}>
                <button
                  onClick={() => handleLanguageChange(language)}
                  className={classNames(
                    'flex w-full items-center gap-3 px-4 py-3 text-sm',
                    // Larger touch target for mobile
                    'min-h-[48px]',
                    'transition-colors duration-150',
                    currentLanguage.languageCode === language.languageCode
                      ? 'bg-indigo-600 text-white'
                      : 'text-gray-900 hover:bg-gray-100',
                  )}
                  type="button"
                >
                  <img
                    src={`https://flagcdn.com/24x18/${language.flag}.png`}
                    srcSet={`https://flagcdn.com/48x36/${language.flag}.png 2x, https://flagcdn.com/72x54/${language.flag}.png 3x`}
                    width="24"
                    height="18"
                    alt={language.country}
                    className="rounded shadow-sm"
                  />
                  <div className="flex-1 text-left">
                    <div className="font-medium">{language.name}</div>
                    <div
                      className={classNames(
                        'text-xs',
                        currentLanguage.languageCode === language.languageCode
                          ? 'text-indigo-100'
                          : 'text-gray-500',
                      )}
                    >
                      {language.country}
                    </div>
                  </div>
                  {currentLanguage.languageCode === language.languageCode && (
                    <svg
                      className="h-5 w-5 text-white"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}
    </li>
  );
}
